import { Component, Input, OnChanges } from "@angular/core";
import { User, userToString } from "src/services/user/user.interface";
import { Item } from "src/services/item/item.interface";
import { Order } from "src/services/order/order.interface";
import { DriverApiService } from "src/services/driver/driver-api.service";
import { rackToString } from "src/services/item/rack.interface";
import { itemCategoryToString } from "src/services/item/item-display";
import { PendingOrder } from "src/services/pending-order/pending-order.interface";
import { tryParseDate } from "src/services/utils/timestamp.utils";
import { isoStringToLocalDate } from "src/services/utils/date.utils";
import { InAppBrowser } from "@awesome-cordova-plugins/in-app-browser/ngx";

@Component({
  selector: "rc-add-to-calendar",
  templateUrl: "./add-to-calendar-button.component.html",
  styleUrls: ["./add-to-calendar-button.component.scss"],
  standalone: false,
})
export class AddToCalendarButtonComponent implements OnChanges {
  public description: string;
  public driverEmail: string | undefined;
  public endTime: string;
  @Input() public items: Item[];
  @Input() public order: Order | PendingOrder;
  public startTime: string;
  public readonly tryParseDate = tryParseDate;
  @Input() public user: User;
  public readonly userToString = userToString;

  constructor(
    private readonly driverService: DriverApiService,
    private readonly inAppBrowser: InAppBrowser,
  ) {}

  public async ngOnChanges(): Promise<void> {
    if (!this.order) {
      return;
    }

    const separator = "-------[br]";
    let itemDescription: string;
    if (this.items) {
      itemDescription = this.items
        .map((item) => {
          const itemDescription = `${item.numberId} - Category: ${itemCategoryToString(item.category)}[br]`;
          const location = `Location: ${rackToString(item)}[br]`;

          return itemDescription + location;
        })
        .join(separator);
    } else {
      itemDescription = "No items in this order at the time this event was created[br]";
    }

    const details =
      `Accommodation: ${this.order.preferences?.accommodation ?? "None"}[br]` +
      `Details: ${this.order.preferences?.details ?? "None"}[br]` +
      `Instructions: ${this.order.preferences?.instruction ?? "None"}[br]` +
      `Hotel Name: ${this.order.preferences?.hotelName ?? "None"}[br]` +
      `Unit Number: ${this.order.preferences?.unitNumber ?? "None"}[br]`;

    this.description = itemDescription + separator + details;

    if (this.order?.driverId) {
      try {
        const driver = await this.driverService.getByUserId(this.order.driverId);
        this.driverEmail = driver ? `${driver.firstName}|${driver.email}` : undefined;
      } catch {
        this.driverEmail = undefined;
      }
    }

    this.startTime = this.order?.fulfillmentTime?.split("-")[0]?.trim();
    this.endTime = this.order?.fulfillmentTime?.split("-")[1]?.trim();

    this.startTime = this.startTime?.padStart(5, "0");
    this.endTime = this.endTime?.padStart(5, "0");
  }

  public openGoogleCalendar(): void {
    if (!this.order) {
      return;
    }

    const driverEmailAddress = this.driverEmail?.split("|")[1];

    const eventTitle = `${userToString(this.user)} - ${this.order.serviceType}`;

    const eventDescription = this.description?.replace(/\[br\]/g, "\n");

    const fulfillmentDate = this.order.fulfillmentDate;
    const startDateTime = this.formatGoogleCalendarDate(fulfillmentDate, this.startTime);
    const endDateTime = this.formatGoogleCalendarDate(fulfillmentDate, this.endTime);

    const attendees: string[] = ["sofia@rocketcloset.com"];
    if (driverEmailAddress) {
      attendees.push(driverEmailAddress);
    }

    const params = new URLSearchParams({
      action: "TEMPLATE",
      text: eventTitle,
      dates: `${startDateTime}/${endDateTime}`,
      details: eventDescription || "",
      location: this.order.address?.formatted_address || "",
      add: attendees.join(","),
      ctz: "America/Denver",
    });

    const calendarUrl = `https://calendar.google.com/calendar/render?${params.toString()}`;

    this.inAppBrowser.create(calendarUrl, "_system");
  }

  private formatGoogleCalendarDate(date: string, time: string): string {
    const dateObj = isoStringToLocalDate(date);

    const [hours, minutes] = time?.split(":") || ["00", "00"];

    dateObj.setHours(parseInt(hours, 10));
    dateObj.setMinutes(parseInt(minutes, 10));
    dateObj.setSeconds(0);

    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const day = String(dateObj.getDate()).padStart(2, "0");
    const hour = String(dateObj.getHours()).padStart(2, "0");
    const minute = String(dateObj.getMinutes()).padStart(2, "0");
    const second = "00";

    return `${year}${month}${day}T${hour}${minute}${second}`;
  }
}
